import momentDate from "../../assets/landing/moment-date.webp";
import momentGrandparents from "../../assets/landing/moment-grandparents.webp";
import momentDaycare from "../../assets/landing/moment-daycare.webp";
import momentPractice from "../../assets/landing/moment-practice.webp";
import { cn } from "../../lib/cn";
import { Sparkle } from "../decor";
import { Scene } from "./stage/Scene";
import * as fx from "./stage/fx";
import type { Timeline } from "./stage/choreography";

/* Tilts and tape colours are literal class strings so Tailwind's JIT can see them. */
const MOMENTS = [
  {
    img: momentDate,
    alt: "A babysitter reads a picture book to two children on the sofa while their parents wave goodbye from the door",
    title: "Date night",
    body: "The sitter has bedtime, allergies, and your numbers before you're out the door.",
    tilt: "-rotate-[2.5deg]",
    tape: "bg-amber-200/70",
  },
  {
    img: momentGrandparents,
    alt: "Grandparents bake cookies with their two grandchildren while the family dog looks on",
    title: "Grandparents' weekend",
    body: "They keep the list handy; you update it from anywhere, and they always see the latest.",
    tilt: "rotate-[1.5deg]",
    tape: "bg-sky-200/70",
  },
  {
    img: momentDaycare,
    alt: "A dad hugs his toddler at daycare drop-off while a smiling teacher waves hello",
    title: "First day at daycare",
    body: "Share allergies, meds, and nap times with staff on day one — no printouts.",
    tilt: "-rotate-1",
    tape: "bg-rose-200/70",
  },
  {
    img: momentPractice,
    alt: "A coach high-fives a young soccer player as her mom cheers with a little brother on her hip",
    title: "Practice & playdates",
    body: "Coaches and other parents get emergency contacts, only for as long as they need them.",
    tilt: "rotate-[2.5deg]",
    tape: "bg-emerald-200/70",
  },
] as const;

/*
 * Signature: "Polaroid deal + develop". The headline slides in line by line
 * and a hand-drawn squiggle underlines "big or small"; then the snapshots are
 * dealt onto the table from the left, one at a time, each photo developing
 * before its caption appears. `<+x` starts a step x seconds after the
 * previous one STARTED, so the cards land at an even pace.
 */
const TIMELINE: Timeline = [
  fx.fade("[data-fx=eyebrow]", { duration: 0.35 }),
  fx.slide("[data-fx=line]", { x: -56, duration: 0.55, stagger: 0.14, at: "-0.2" }),
  fx.draw("[data-fx=squiggle]", { duration: 0.5, at: "-0.2" }),
  ...MOMENTS.flatMap((_, i) => [
    // Thrown from off-stage left, so the far cards fly further.
    fx.deal(`[data-fx=card-${i}]`, { x: -(720 + 260 * i), rotate: -24, at: i === 0 ? "-0.2" : "<+0.02" }),
    fx.develop(`[data-fx=photo-${i}]`, { duration: 0.75, at: "<+0.2" }),
    fx.fade(`[data-fx=cap-${i}]`, { y: 8, duration: 0.4, at: "<+0.16" }),
  ]),
];

export function RealMoments() {
  return (
    <Scene
      id="moments"
      label="Real moments"
      timeline={TIMELINE}
      className="bg-gradient-to-b from-emerald-50/70 via-white to-sky-50"
    >
      {/* Backdrop: mint wash, soft colour fields and a whisper of grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-32 -top-24 h-[28rem] w-[28rem] rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute -right-40 top-1/4 h-[30rem] w-[30rem] rounded-full bg-sky-200/45 blur-3xl" />
        <div className="absolute -bottom-28 left-1/3 h-80 w-80 rounded-full bg-amber-100/60 blur-3xl" />
        <Sparkle className="absolute right-[10%] top-[18%] hidden h-5 w-5 text-emerald-300 sm:block" />
        <Sparkle className="absolute bottom-[10%] left-[5%] hidden h-4 w-4 text-amber-300 sm:block" />
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-6 pb-10 pt-20">
        <div>
          <p data-fx="eyebrow" className="text-sm font-semibold uppercase tracking-wide text-emerald-700">
            Made for real moments
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold leading-[1.1] tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl">
            <span data-fx="line" className="block">
              For every hand-off,
            </span>{" "}
            <span data-fx="line" className="block">
              <span className="relative inline-block">
                big or small
                <svg
                  aria-hidden="true"
                  viewBox="0 0 300 24"
                  fill="none"
                  className="pointer-events-none absolute -bottom-[0.3em] left-[-2%] h-auto w-[104%] overflow-visible"
                >
                  <defs>
                    <linearGradient id="moments-squiggle" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="300" y2="0">
                      <stop offset="0" stopColor="#34d399" />
                      <stop offset="1" stopColor="#38bdf8" />
                    </linearGradient>
                  </defs>
                  <path
                    data-fx="squiggle"
                    d="M4 15 C 24 6, 42 7, 60 13 S 96 21, 118 13 S 156 5, 178 12 S 216 20, 240 12 S 278 6, 296 10"
                    stroke="url(#moments-squiggle)"
                    strokeWidth={6}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              .
            </span>
          </h2>
        </div>

        {/* 2×2 until there's room for a row of four; capped on tablets so two rows still fit the screen. */}
        <ul
          role="list"
          className="mt-8 grid grid-cols-2 items-start gap-x-4 gap-y-5 sm:mt-10 sm:max-w-xl sm:gap-x-6 lg:mt-12 lg:max-w-none lg:grid-cols-4 lg:gap-x-8"
        >
          {MOMENTS.map((moment, i) => (
            <li key={moment.title} data-fx={`card-${i}`} className={cn("group", i % 2 === 1 && "lg:mt-8")}>
              {/* The tilt lives here, not on the dealt card; hover straightens and lifts it. */}
              <div
                className={cn(
                  "relative transition-transform duration-300 ease-out group-hover:-translate-y-2 group-hover:rotate-0",
                  moment.tilt,
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute -top-2.5 left-1/2 z-10 h-5 w-14 -translate-x-1/2 -rotate-3 rounded-[3px] shadow-sm sm:w-16",
                    moment.tape,
                  )}
                />
                <figure className="rounded-md bg-white p-1.5 pb-3 shadow-[0_22px_40px_-22px_rgba(15,23,42,0.45)] ring-1 ring-neutral-900/5 transition-shadow duration-300 group-hover:shadow-[0_32px_50px_-24px_rgba(15,23,42,0.5)] sm:p-2.5 sm:pb-4">
                  <div className="overflow-hidden rounded-sm bg-neutral-100">
                    <img
                      data-fx={`photo-${i}`}
                      src={moment.img}
                      alt={moment.alt}
                      width={760}
                      height={568}
                      loading="lazy"
                      draggable={false}
                      className="block aspect-[4/3] w-full select-none object-cover"
                    />
                  </div>
                  <figcaption data-fx={`cap-${i}`} className="px-1 pt-2 sm:px-1.5 sm:pt-3">
                    <h3 className="font-display text-sm font-semibold leading-snug text-neutral-900 sm:text-base lg:text-lg">
                      {moment.title}
                    </h3>
                    <p className="mt-1 line-clamp-5 text-pretty text-xs leading-snug text-neutral-600 sm:line-clamp-none sm:text-sm">
                      {moment.body}
                    </p>
                  </figcaption>
                </figure>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Scene>
  );
}
